import React, { useMemo, useState } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import QuadrantDetailModal from './QuadrantDetailModal'; // Import the new modal component

export default function PerformanceVsCulturalFit({ data }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedQuadrant, setSelectedQuadrant] = useState({ performance: '', fit: '', count: 0 });

  const quadrantData = useMemo(() => {
    // If data is empty or null, return an empty object to prevent errors in subsequent rendering
    if (!data || Object.keys(data).length === 0) return {};
    return data; // Data is already in the desired quadrant format
  }, [data]);

  // Calculate max value for color scaling, moved here to be unconditional
  const maxValue = useMemo(() => {
    let max = 0;
    if (quadrantData) {
      Object.keys(quadrantData).forEach(perfCategory => {
        Object.keys(quadrantData[perfCategory]).forEach(fitCategory => {
          if (quadrantData[perfCategory][fitCategory] > max) {
            max = quadrantData[perfCategory][fitCategory];
          }
        });
      });
    }
    return max;
  }, [quadrantData]);

  // Helper function to get color based on value (simple linear scale for now)
  const getColorForValue = (value, maxValue) => {
    if (maxValue === 0) return '#ffffff'; // White if no data
    const intensity = value / maxValue;
    // Example: A light blue to dark blue scale
    const r = 240 - (100 * intensity);
    const g = 248 - (100 * intensity);
    const b = 255 - (50 * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleCellClick = (performanceCategory, fitCategory, count) => {
    setSelectedQuadrant({ performance: performanceCategory, fit: fitCategory, count });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  if (!data || Object.keys(data).length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: '12px', textAlign: 'center' }}>
        <Typography variant="subtitle1">No hay datos de desempeño vs. fit cultural disponibles para el período seleccionado.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: '12px' }}>
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fit Cultural Alto</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fit Cultural Medio</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Fit Cultural Bajo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {['Alto', 'Medio', 'Bajo'].map(perfCategory => (
              <TableRow key={perfCategory}>
                <TableCell sx={{ fontWeight: 'bold' }}>Desempeño {perfCategory}</TableCell>
                <TableCell 
                  align="center" 
                  sx={{
                    backgroundColor: getColorForValue(quadrantData[perfCategory]?.Alto || 0, maxValue),
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => handleCellClick(perfCategory, 'Alto', quadrantData[perfCategory]?.Alto || 0)}
                >
                  {quadrantData[perfCategory]?.Alto || 0}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{
                    backgroundColor: getColorForValue(quadrantData[perfCategory]?.Medio || 0, maxValue),
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => handleCellClick(perfCategory, 'Medio', quadrantData[perfCategory]?.Medio || 0)}
                >
                  {quadrantData[perfCategory]?.Medio || 0}
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{
                    backgroundColor: getColorForValue(quadrantData[perfCategory]?.Bajo || 0, maxValue),
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => handleCellClick(perfCategory, 'Bajo', quadrantData[perfCategory]?.Bajo || 0)}
                >
                  {quadrantData[perfCategory]?.Bajo || 0}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <QuadrantDetailModal
        open={modalOpen}
        handleClose={handleModalClose}
        performanceCategory={selectedQuadrant.performance}
        fitCategory={selectedQuadrant.fit}
        count={selectedQuadrant.count}
      />
    </Paper>
  );
} 